import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScanLine, CameraOff } from 'lucide-react';

/** Camera barcode scanner using the native BarcodeDetector where available, degrading to a manual code box. */
const BARCODE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code'];

export default function BarcodeScanner({ open, onClose, onDetected }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const detectorRef = useRef(null);
  const lastHitRef = useRef({ code: '', at: 0 });

  const supported = typeof window !== 'undefined' && 'BarcodeDetector' in window;
  const [status, setStatus] = useState('idle'); // idle | running | error
  const [error, setError] = useState('');
  const [manual, setManual] = useState('');
  const [lastCode, setLastCode] = useState('');

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStatus('idle');
  }, []);

  const handleHit = useCallback((code) => {
    if (!code) return;
    const now = Date.now();
    // Debounce repeats of the same barcode so one bottle isn't counted twice.
    if (code === lastHitRef.current.code && now - lastHitRef.current.at < 1500) return;
    lastHitRef.current = { code, at: now };
    setLastCode(code);
    onDetected(code);
  }, [onDetected]);

  const scanLoop = useCallback(async () => {
    if (!videoRef.current || !detectorRef.current) return;
    try {
      const codes = await detectorRef.current.detect(videoRef.current);
      if (codes && codes.length) handleHit(codes[0].rawValue);
    } catch {
      /* transient decode error — keep going */
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  }, [handleHit]);

  const start = useCallback(async () => {
    if (!supported) return;
    setError('');
    try {
      const Detector = window.BarcodeDetector;
      const formats = (await Detector.getSupportedFormats?.()) || BARCODE_FORMATS;
      detectorRef.current = new Detector({
        formats: BARCODE_FORMATS.filter((f) => formats.includes(f)),
      });
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStatus('running');
      rafRef.current = requestAnimationFrame(scanLoop);
    } catch (e) {
      setStatus('error');
      setError(e?.message || 'Could not start the camera. Check camera permissions.');
    }
  }, [supported, scanLoop]);

  useEffect(() => {
    if (open && supported) start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submitManual = () => {
    const code = manual.trim();
    if (!code) return;
    handleHit(code);
    setManual('');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { stop(); onClose(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5" /> Scan a bottle
          </DialogTitle>
        </DialogHeader>

        {supported ? (
          <div className="space-y-3">
            <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
              <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
              <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 h-0.5 bg-red-500/80" />
            </div>
            {status === 'error' && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            {lastCode && (
              <p className="text-xs text-muted-foreground font-mono">Last read: {lastCode}</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md bg-amber-50 text-amber-800 p-3 text-sm">
              <CameraOff className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Camera scanning isn't supported on this browser — type the barcode below or use a USB/Bluetooth scanner.</span>
            </div>
          </div>
        )}

        {/* Manual entry is always available — also catches hardware scanners. */}
        <div className="space-y-2">
          <Label htmlFor="manual-barcode">Enter barcode manually</Label>
          <div className="flex gap-2">
            <Input
              id="manual-barcode"
              value={manual}
              autoFocus={!supported}
              placeholder="e.g. 5000267023656"
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitManual(); } }}
            />
            <Button onClick={submitManual} disabled={!manual.trim()}>Find</Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { stop(); onClose(); }}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
